import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, RefreshCw, Eye, ChevronDown, Check } from "lucide-react";
import { Card, CardBody } from "../components/Card";
import { Button } from "../components/Button";
import { api } from "../lib/api";
import { useMe } from "../lib/me";
import type { CaseSummary } from "../lib/me";
import { cn } from "../lib/cn";
import { Pagination } from "../components/Pagination";

const STATUS_LABEL: Record<string, string> = {
  new: "New",
  classified: "Classified",
  below_threshold: "Low confidence",
  awaiting_slot_fill: "Awaiting reply",
  awaiting_review: "Awaiting review",
  awaiting_reply: "Awaiting reply",
  resolved: "Resolved",
  escalated: "Escalated",
  error: "Error",
};

const STATUS_COLOR: Record<string, string> = {
  new: "bg-slate-100 text-slate-700 border-slate-200/50",
  classified: "bg-blue-50/70 text-blue-700 border-blue-200/30",
  below_threshold: "bg-amber-50/70 text-amber-700 border-amber-200/30",
  awaiting_slot_fill: "bg-amber-50/70 text-amber-700 border-amber-200/30",
  awaiting_review: "bg-amber-50/70 text-amber-700 border-amber-200/30",
  awaiting_reply: "bg-sky-50/70 text-sky-700 border-sky-200/30",
  resolved: "bg-emerald-50/70 text-emerald-700 border-emerald-200/30",
  escalated: "bg-rose-50/70 text-rose-700 border-rose-200/30",
  error: "bg-rose-50/70 text-rose-700 border-rose-200/30",
};

const STATUS_TABS: Array<{ value: string | undefined; label: string }> = [
  { value: undefined, label: "All statuses" },
  { value: "awaiting_review", label: "Awaiting Review" },
  { value: "awaiting_slot_fill", label: "Awaiting Reply" },
  { value: "resolved", label: "Resolved" },
  { value: "escalated", label: "Escalated" },
];

interface PaginatedCases {
  data: CaseSummary[];
  pagination: { nextCursor: string | null; hasMore: boolean; total: number };
}

export default function Cases() {
  const navigate = useNavigate();
  const { me } = useMe();
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState<PaginatedCases | null>(null);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const fetchPage = useCallback(
    async (cursor: string | null, statusFilter: string | undefined, currentLimit: number) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("limit", String(currentLimit));
        if (cursor) params.set("cursor", cursor);
        if (statusFilter) params.set("status", statusFilter);
        const result = await api.getRaw<PaginatedCases>(`/api/cases?${params.toString()}`);
        setData(result);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    setCursors([null]);
    setPage(0);
    fetchPage(null, status, limit);
  }, [status, limit, fetchPage]);

  const handlePageChange = async (targetPage: number) => {
    if (targetPage === page || loading) return;

    if (targetPage < cursors.length) {
      setPage(targetPage);
      await fetchPage(cursors[targetPage] ?? null, status, limit);
    } else {
      setLoading(true);
      try {
        let currentCursor = data?.pagination.nextCursor;
        let tempCursors = [...cursors];
        let tempPage = page;
        let tempData = data;

        while (tempPage < targetPage && currentCursor) {
          const params = new URLSearchParams();
          params.set("limit", String(limit));
          if (currentCursor) params.set("cursor", currentCursor);
          if (status) params.set("status", status);

          const result = await api.getRaw<PaginatedCases>(`/api/cases?${params.toString()}`);
          tempCursors.push(currentCursor);
          currentCursor = result.pagination.nextCursor;
          tempPage++;
          tempData = result;
        }

        setCursors(tempCursors);
        setPage(tempPage);
        setData(tempData);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRefresh = () => {
    fetchPage(cursors[page] ?? null, status, limit);
  };

  const activeStatusLabel = STATUS_TABS.find((t) => t.value === status)?.label ?? "All statuses";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Cases</h2>
          <p className="mt-1 text-sm text-slate-500 font-medium">
            All conversations Triage has handled or is handling. Newest received first.
          </p>
        </div>
        
        {/* Fancy Custom Dropdown Filter & Refresh */}
        <div className="flex items-center gap-3.5 self-start sm:self-center">
          <div className="flex items-center gap-2.5">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider select-none">Status:</span>
            <div className="relative inline-block text-left">
              <div>
                <button
                  type="button"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="inline-flex h-9 w-40 justify-between items-center gap-x-1.5 rounded-xl bg-white px-3.5 text-xs font-bold text-slate-700 shadow-2xs ring-1 ring-inset ring-slate-200/80 hover:bg-slate-50 hover:ring-slate-300 active:scale-[0.98] transition-all cursor-pointer select-none"
                >
                  <span className="truncate">{activeStatusLabel}</span>
                  <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" aria-hidden="true" />
                </button>
              </div>

              {dropdownOpen && (
                <>
                  {/* Backdrop overlay to close dropdown */}
                  <div className="fixed inset-0 z-10" onClick={() => setDropdownOpen(false)} />
                  
                  {/* Floating Menu */}
                  <div className="absolute right-0 z-20 mt-2 w-48 origin-top-right rounded-xl bg-white shadow-md ring-1 ring-black/5 divide-y divide-slate-100 overflow-hidden animate-scale-in">
                    <div className="py-1">
                      {STATUS_TABS.map((tab) => {
                        const isActive = status === tab.value;
                        return (
                          <button
                            key={tab.label}
                            type="button"
                            onClick={() => {
                              setStatus(tab.value);
                              setDropdownOpen(false);
                            }}
                            className={cn(
                              "flex w-full items-center justify-between px-4 py-2 text-left text-xs font-bold transition-colors cursor-pointer select-none",
                              isActive
                                ? "bg-brand-50 text-brand-700"
                                : "text-slate-700 hover:bg-slate-50"
                            )}
                          >
                            <span>{tab.label}</span>
                            {isActive && <Check className="h-3.5 w-3.5 text-brand-600" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <button
            onClick={handleRefresh}
            disabled={loading}
            className="inline-flex h-9 items-center justify-center rounded-xl bg-white px-3.5 text-xs font-bold text-slate-700 shadow-2xs ring-1 ring-inset ring-slate-200/80 hover:bg-slate-50 hover:ring-slate-300 active:scale-[0.98] transition-all cursor-pointer select-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 shrink-0 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {!data || (loading && data.data.length === 0) ? (
        <div className="flex h-60 items-center justify-center">
          <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : data.data.length === 0 ? (
        <Card>
          <CardBody>
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 border border-slate-200/60 text-slate-400">
                <Inbox className="h-5.5 w-5.5" />
              </div>
              <p className="mt-4 text-base font-bold text-slate-900">No cases found</p>
              <p className="mt-1.5 max-w-sm text-sm text-slate-500 font-medium leading-normal">
                {status
                  ? `Nothing in '${STATUS_LABEL[status] ?? status}' right now.`
                  : "Send an email to your inbound address to see your first case appear here."}
              </p>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-4">
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px] border-collapse text-left">
                <thead>
                  <tr className="bg-slate-900 border-b border-slate-950 text-white select-none">
                    <th className="px-6 py-3.5 text-[11px] font-bold uppercase tracking-wider first:rounded-tl-2xl">Subject</th>
                    <th className="px-6 py-3.5 text-[11px] font-bold uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3.5 text-[11px] font-bold uppercase tracking-wider">Category & Match</th>
                    <th className="px-6 py-3.5 text-[11px] font-bold uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3.5 text-[11px] font-bold uppercase tracking-wider text-right">Received</th>
                    <th className="px-6 py-3.5 text-[11px] font-bold uppercase tracking-wider last:rounded-tr-2xl text-right w-16">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {data.data.map((kase) => (
                    <tr
                      key={kase.caseId}
                      onClick={() => navigate(`/cases/${kase.caseId}`)}
                      className="group cursor-pointer hover:bg-slate-50/50 transition-colors"
                    >
                      {/* Subject Details */}
                      <td className="px-6 py-3.5 min-w-0 max-w-[240px]">
                        <h3 className="truncate text-sm font-semibold text-slate-900 group-hover:text-brand-600 transition-colors">
                          {kase.subject}
                        </h3>
                      </td>

                      {/* Customer Column */}
                      <td className="px-6 py-3.5 min-w-0 max-w-[220px]">
                        <p className="truncate text-sm font-semibold text-slate-800">
                          {kase.fromName ?? kase.fromEmail}
                        </p>
                        <p className="truncate text-xs font-normal text-slate-400 mt-0.5">
                          {kase.fromEmail}
                        </p>
                      </td>

                      {/* Category & Match */}
                      <td className="px-6 py-3.5">
                        <div className="flex items-center flex-wrap gap-1.5">
                          {kase.intentName ? (
                            <span className="rounded-md bg-brand-50 border border-brand-200/40 px-2 py-0.5 text-[11px] font-medium text-brand-700 truncate max-w-full">
                              {kase.intentName}
                            </span>
                          ) : (
                            <span className="text-[11px] text-slate-400 font-medium italic">Unclassified</span>
                          )}
                          {kase.classificationConfidence != null && (
                            <ConfidencePill value={kase.classificationConfidence} />
                          )}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-3.5">
                        <StatusPill status={kase.status} />
                      </td>

                      {/* Received Timestamp */}
                      <td className="px-6 py-3.5 text-right">
                        <span className="text-xs font-medium text-slate-500">
                          {formatDateTime(kase.createdAt)}
                        </span>
                      </td>

                      {/* Actions Button */}
                      <td className="px-6 py-3.5 text-right w-16">
                        <button className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 hover:bg-brand-100 text-brand-600 transition-colors select-none cursor-pointer">
                          <Eye className="h-4 w-4 shrink-0" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Pagination
            currentPage={page}
            totalRecords={data.pagination.total ?? 0}
            limit={limit}
            loading={loading}
            onPageChange={handlePageChange}
            onLimitChange={(newLimit) => setLimit(newLimit)}
          />
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const cls = STATUS_COLOR[status] ?? "bg-slate-100 text-slate-700 border-slate-200/50";
  const label = STATUS_LABEL[status] ?? status;
  return <span className={`rounded-md border px-2 py-0.5 text-[11px] font-medium ${cls}`}>{label}</span>;
}

function ConfidencePill({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const tone =
    value >= 0.8
      ? "bg-emerald-50 text-emerald-700 border-emerald-200/50"
      : value >= 0.6
      ? "bg-amber-50 text-amber-700 border-amber-205/50"
      : "bg-rose-50 text-rose-700 border-rose-200/50";
  return <span className={`rounded-md border px-2 py-0.5 text-[11px] font-medium ${tone}`}>{pct}% Match</span>;
}

function formatDateTime(dateInput: string | Date): string {
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const hoursStr = String(hours).padStart(2, "0");
  return `${year}-${month}-${day} ${hoursStr}:${minutes} ${ampm}`;
}
